import { Helmet } from 'react-helmet-async'
import HomeTemplate from '../templates/HomeTemplate.jsx'
import VideoDownloaderSEO from '../components/ui/VideoDownloaderSEO.jsx'
import { SITE_URL } from '../utils/seo.js'
import { useLanguage } from '../context/LanguageContext.jsx'

const title       = 'TikTok Video Downloader — No Watermark, Free MP4 & MP3 | FastAudio'
const description = 'Download TikTok videos as MP4 without watermark or as MP3 audio — free, no login required. Works on iPhone, Android & PC in seconds.'
const canonical   = `${SITE_URL}/tiktok-video-downloader`

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'Can I download TikTok videos without watermark?',   acceptedAnswer: { '@type': 'Answer', text: 'Yes. FastAudio downloads the original TikTok video stream without the username/watermark overlay.' } },
    { '@type': 'Question', name: 'Is the TikTok downloader free?',                    acceptedAnswer: { '@type': 'Answer', text: 'Yes, completely free with no registration or account required.' } },
    { '@type': 'Question', name: 'Does FastAudio support vm.tiktok.com short links?', acceptedAnswer: { '@type': 'Answer', text: 'Yes, both standard TikTok URLs and short links (vm.tiktok.com, vt.tiktok.com) are supported.' } },
    { '@type': 'Question', name: 'What quality are downloaded TikTok videos?',        acceptedAnswer: { '@type': 'Answer', text: 'Most TikTok videos are stored at 720p HD. FastAudio downloads the best available resolution from TikTok\'s servers.' } },
    { '@type': 'Question', name: 'Can I download TikTok audio as MP3?',               acceptedAnswer: { '@type': 'Answer', text: 'Yes. Select an MP3 quality option to extract just the audio track from any public TikTok video.' } },
  ],
}

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to Download TikTok Videos Without Watermark',
  step: [
    { '@type': 'HowToStep', name: 'Open the TikTok video',  text: 'Find the video in the TikTok app or on tiktok.com.' },
    { '@type': 'HowToStep', name: 'Copy the share link',    text: 'Tap Share → Copy Link in the TikTok app.' },
    { '@type': 'HowToStep', name: 'Paste into FastAudio',   text: 'Paste the URL into the FastAudio search box.' },
    { '@type': 'HowToStep', name: 'Select format',          text: 'Choose MP4 (720p HD) or an MP3 audio quality.' },
    { '@type': 'HowToStep', name: 'Click Download',         text: 'Hit ⚡ Download — the watermark-free file saves to your device.' },
  ],
}

export default function TikTokVideoDownloader() {
  const { t } = useLanguage()
  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title"       content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url"         content={canonical} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
      </Helmet>
      <HomeTemplate
        provider="tiktok"
        title={title}
        description={description}
        canonical={canonical}
        badge="TikTok Video Downloader"
        heading={<>TikTok <span className="text-gradient">No Watermark</span> Downloader</>}
        subheading="Download TikTok videos as clean MP4 (watermark-free, 720p HD) or MP3 audio — no TikTok account needed, works on all devices."
        placeholder={t('ttPlaceholder')}
      >
        <VideoDownloaderSEO provider="tiktok" />
      </HomeTemplate>
    </>
  )
}
